import {
  Button,
  Callout,
  Card,
  FormLayout,
  FormLayoutProvider,
  Heading,
  Paragraph,
  PasswordInput,
  RadioGroup,
  SyntaxIcon,
  Text,
  TextInput,
  useDialog,
  useToast,
} from '@expressable/syntax-ds';
import { SealQuestion } from '@phosphor-icons/react';
import { useCognitoUserContext } from 'domains/auth/AWSCognitoWrapper/context';
import { SignupLoginModal } from 'domains/auth/SignupLoginModal/SignupLoginModal';
import { useContactById } from 'domains/contact/hooks';
import { FC, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { SignupError } from 'utils/errors/debuggableError';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from '../misc/mixpanelTracking';
import { BaseContactInfoSchema } from '../schemas/common';
import { EvaluationFormType } from '../schemas/evaluationSchema';
import { HeadingCombo } from '../SyntaxComps/HeadingCombo';
import { useFieldErrors } from 'utils/forms/fieldErrors';
import { SignupEmailErrorCallout } from '../Evaluation/EmailError/EmailError';
import { isUndefined } from 'lodash';

type Props = {
  onLoginPrompt?: () => void;
  onLogoutPrompt?: () => void;
};

export const SignupLoginGate: FC<Props> = ({ onLoginPrompt, onLogoutPrompt, children }) => {
  const cognito = useCognitoUserContext();

  const { data: contact } = useContactById({ contactId: cognito.isLoggedIn ? cognito.user.username : undefined });

  const { dialogStateProps, onOpen } = useDialog();

  const logoutHandler = useCallback(() => {
    onLogoutPrompt?.();
    if (cognito.isLoggedIn) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGOUT);
      cognito.signOut();
    }
  }, [cognito, onLogoutPrompt]);

  const openLoginHandler = useCallback(() => {
    onLoginPrompt?.();
    onOpen();
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.TRIGGERED_MODAL);
  }, [onLoginPrompt, onOpen]);

  const { t } = useTranslation();

  const { track } = useTrack();

  useEffectOnce(() => {
    if (cognito.isLoggedIn) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.LOGGED_IN);
    } else {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.LOGGED_OUT);
    }
  });

  const { showToast } = useToast();

  /** Check for bad data on contact, force logout and throw error
   * More graceful handling of this will be added in the future
   */
  useEffect(() => {
    if (cognito.isLoggedIn && contact) {
      const parseResult = BaseContactInfoSchema.required().safeParse({
        firstName: contact?.contactFirstName,
        lastName: contact?.contactLastName,
        email: contact?.email,
        phone: contact?.phone?.mobile ?? '',
        contactID: contact?.contactID,
      });

      if (!parseResult.success) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.FAILURE);
        logoutHandler();

        showToast({ variant: 'error', message: t('directSignup.loginGate.logoutToast'), duration: 8000 });
        throw new SignupError('Contact data is invalid, not passing validation. Forcing log out', {
          debugData: {
            parseResult,
            contact,
          },
        });
      }
    }
  }, [cognito, contact]);

  const isAlreadyContact = useWatch<EvaluationFormType, 'loginContactInfo.isAlreadyContact'>({
    name: 'loginContactInfo.isAlreadyContact',
  });
  useEffect(() => {
    if (cognito.isLoggedIn) return;
    if (isUndefined(isAlreadyContact)) return;

    if (isAlreadyContact) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.PICKED_EXISTING_CONTACT);
    } else {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.PICKED_NEW_CONTACT);
    }
  }, [isAlreadyContact, cognito.isLoggedIn]);

  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<EvaluationFormType>();

  const fieldErrors = useFieldErrors('loginContactInfo.email', errors);
  const isEmailExistsError = fieldErrors?.includes(t('directSignup.serverErrors.EMAIL_EXISTS.base'));

  const onClickLogin = useCallback(() => {
    setValue('loginContactInfo.isAlreadyContact', true);
  }, [setValue]);

  const NewClientVariant = (
    <FormLayout.Fields className="p-e32">
      <HeadingCombo>
        <Heading level="h5" noDefaultMargins>
          {t('directSignup.loginGate.content.no.title')}
        </Heading>
        <Paragraph variant="secondary" size="md">
          {t('directSignup.loginGate.content.no.subTitle')}
        </Paragraph>
      </HeadingCombo>

      {isEmailExistsError && <SignupEmailErrorCallout onClickLogin={onClickLogin} />}

      <TextInput
        {...register('loginContactInfo.email')}
        label={t('directSignup.loginGate.content.no.emailLabel')}
        type="email"
      />
      <PasswordInput
        {...register('loginContactInfo.newPassword')}
        label={t('directSignup.loginGate.content.no.passwordLabel')}
        hint={t('directSignup.loginGate.content.no.passwordHint')}
      />
    </FormLayout.Fields>
  );

  const ExistingClientVariant = (
    <div className="flex flex-col gap-e32 p-e32">
      <Paragraph noDefaultMargins>{t('directSignup.loginGate.content.yes.body')}</Paragraph>
      <Button variant="secondary" onClick={openLoginHandler}>
        {t('directSignup.loginGate.content.yes.button')}
      </Button>
    </div>
  );

  if (cognito.isLoggedIn) {
    return (
      <>
        <Callout
          type={cognito.isLoggedIn ? 'info' : 'default'}
          className="w-full"
          data-testid="signUp-loginChecker-logged-in"
        >
          <Text variant="body-md" data-testid="signUp-loginChecker-logged-in-title">
            {t('directSignup.loginGate.loggedIn.title')}

            <span className="font-bold" data-testid="signUp-loginChecker-contact-name">
              {contact?.contactFirstName} {contact?.contactLastName}
            </span>
          </Text>
          <Text variant="body-sm">
            {t('directSignup.loginGate.loggedIn.notYou')}{' '}
            <Button variant="link" size="md" onClick={logoutHandler} data-testid="signUp-logout">
              {t('directSignup.loginGate.loggedIn.signOut')}
            </Button>
          </Text>
        </Callout>
        {children}
      </>
    );
  }

  return (
    <>
      <Card
        className="w-full flex flex-col p-0 divide-y divide-warm-gray-200"
        data-testid="signUp-loginChecker-logged-out"
      >
        <div className="flex flex-col gap-e32 p-e32">
          <Heading level="h6" className="flex items-center gap-e16" noDefaultMargins>
            <div className="flex items-center justify-center rounded-full size-e40 bg-orchid-50">
              <SyntaxIcon icon={SealQuestion} weight="fill" className="text-purple-500" size="lg" />
            </div>
            {t('directSignup.loginGate.question.label')}
          </Heading>
          <RadioGroup name="loginContactInfo.isAlreadyContact" standalone>
            <RadioGroup.Option variant="plain" value={false}>
              {t('directSignup.loginGate.question.no')}
            </RadioGroup.Option>

            <RadioGroup.Option variant="plain" value={true}>
              {t('directSignup.loginGate.question.yes')}
            </RadioGroup.Option>
          </RadioGroup>
        </div>
        <FormLayoutProvider variant="compact">
          {isAlreadyContact === false && NewClientVariant}
          {isAlreadyContact && ExistingClientVariant}
        </FormLayoutProvider>
        <SignupLoginModal {...dialogStateProps} />
      </Card>

      {isAlreadyContact === false && children}
    </>
  );
};
